<template>
  <component
    :is="hasIconOnly ? SbIconButton : SbButton"
    :id="context.menuButtonId"
    :aria-controls="context.menuListId"
    :aria-label="hasIconOnly ? label : hiddenLabel"
    aria-haspopup="true"
    :aria-expanded="isOpen ? 'true' : 'false'"
    v-bind="{
      icon: hasIconOnly ? icon || 'more-horizontal' : icon,
      isDisabled,
      iconRight,
      iconColor,
      label,
      variant,
      size,
      isClear: isBorderless || isClear,
      tooltipWithinTarget,
    }"
    @click="handleClick"
    @keydown="handleKeyDown"
  >
    <slot v-if="!hasIconOnly && hasDefaultSlot" />
  </component>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'
import SbButton from '../Button'
import SbIconButton from '../IconButton'
import { useMenu } from './utils'
import type { AvailableButtonVariants } from '../Button/lib'
import type { AvailableSizes } from '../../utils'
import type { IconColor, IconName } from '../Icon'

type MenuButtonProps = {
  /**
   * The label of the button. This property is required for accessibility concerns
   */
  label: string

  /**
   * The aria-label of the button. This can optionally be used if the the aria label is different from the visible label
   */
  hiddenLabel?: string

  /**
   * Links to SbButton variant
   */
  variant?: AvailableButtonVariants

  /**
   * If true, the button will render only the icon
   */
  hasIconOnly?: boolean

  /**
   * Links to SbButton icon property
   */
  icon?: IconName

  /**
   * Links to SbButton size property
   */
  size?: AvailableSizes

  /**
   * If true, the button will have no border
   */
  isBorderless?: boolean

  /**
   * Links to SbButton isClear property
   */
  isClear?: boolean

  /**
   * Links to SbButton iconRight property
   */
  iconRight?: string

  /**
   * If true, the button will be disabled
   */
  isDisabled?: boolean

  /**
   * Links to SbButton iconColor property
   */
  iconColor?: IconColor

  /**
   * If true, the tooltip will be rendered within the target element
   */
  tooltipWithinTarget?: boolean
}

const props = withDefaults(defineProps<MenuButtonProps>(), {
  variant: 'primary',
  iconRight: undefined,
  size: 'normal',
  icon: undefined,
  iconColor: undefined,
  tooltipWithinTarget: false,
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
  (e: 'keydown', event: KeyboardEvent): void
}>()

const { context } = useMenu()

const isOpen = computed(() => context.value.isOpen || false)
const hasDefaultSlot = computed(() => !!useSlots().default)

const handleClick = (event: MouseEvent) => {
  const { closeMenu, openMenu } = context.value

  emit('click', event)

  event.preventDefault()

  if (isOpen.value) {
    closeMenu()
  } else {
    openMenu()
  }
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (props.isDisabled) {
    return
  }

  const { focusOnFirstItem, focusOnLastItem } = context.value

  emit('keydown', event)

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    focusOnLastItem()
  }

  if (event.key === 'ArrowDown' || event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    focusOnFirstItem()
  }
}
</script>
